import { MathKeyboard } from '@hendrx/icons/MathKeyboard';
import { Button, Group, Popover, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { RichTextEditor } from '@mantine/tiptap';
import type { Editor } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getScrollContent, getScrollParent } from 'shared/components/MathKeyboard/keyboardLift';
import { MATH_KEYBOARD_LIFT_PROPS, MathLiveField } from 'shared/components/MathLiveField';

import { INLINE_MATH_NODE_NAME } from '../../../../utils/math/mathNode';

// Space kept between the revealed caret/formula and the editor viewport's edges.
const REVEAL_GAP = 16;

type MathControlProps = {
  editor: Editor | null;
  withinPortal?: boolean;
};

/**
 * Toolbar control that opens a mathlive math keyboard in a popover and inserts the
 * resulting LaTeX into the editor as an `inlineMath` node (rendered via KaTeX by the
 * tiptap math extension).
 */
export function MathControl({ editor, withinPortal = false }: MathControlProps) {
  const { t } = useTranslation('widgets.mathControl');
  const [opened, { open, close }] = useDisclosure(false);
  const [latex, setLatex] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // While the popover is open, scroll the caret/formula the LaTeX will land at into view, without
  // ever letting the toolbar-anchored popover leave the screen (bug 535585).
  useEffect(() => {
    if (!opened || !editor) return undefined;

    // The popover's own upward nudge (negative margin), tracked so re-runs measure the resting box.
    let appliedShift = 0;
    // Last scroll target issued, so a still-in-flight smooth scroll isn't re-issued (a re-issue
    // restarts the ease from zero velocity — the sluggish crawl).
    let lastIssuedTarget = Number.NEGATIVE_INFINITY;
    // Previous pass's scrollTop — tells an in-flight scroll (converging) from a cancelled one
    // (frozen short of the target), which must be re-issued.
    let lastObservedTop = Number.NaN;
    // Extra scroll room below the content; only ever GROWN while open — shrinking it while the
    // scroller sits on it clamps scrollTop and drops the parked caret back down.
    let paddedContent: HTMLElement | null = null;
    let basePaddingStyle = '';
    let basePaddingPx = 0;
    let appliedPadding = 0;

    // One correction pass; returns how far (px) the caret still is from its resting place.
    const reveal = (): number => {
      const { from } = editor.state.selection;
      const dom = editor.view.nodeDOM(from) ?? editor.view.domAtPos(from).node;
      const el = dom instanceof HTMLElement ? dom : dom?.parentElement;
      const scroller = el ? getScrollParent(el) : null;
      if (!el || !scroller) return 0;

      const caret = el.getBoundingClientRect();
      const view = scroller.getBoundingClientRect();
      const dropdownEl = dropdownRef.current;
      const dropdown = dropdownEl?.getBoundingClientRect();
      // A sticky toolbar pins the popover while content scrolls; a non-sticky one carries it along.
      const anchorButton = dropdownEl?.id
        ? document.querySelector<HTMLElement>(`[aria-controls="${dropdownEl.id}"]`)
        : null;
      let anchorSticky = false;
      for (let node = anchorButton; node && node !== scroller; node = node.parentElement) {
        if (getComputedStyle(node).position === 'sticky') {
          anchorSticky = true;
          break;
        }
      }

      const topBoundary = (anchorSticky && dropdown ? Math.max(view.top, dropdown.bottom) : view.top) + REVEAL_GAP;
      let delta = 0;
      if (anchorSticky && dropdown) {
        // Pinned popover: park the caret right below it, whichever direction it comes from.
        delta = caret.top - topBoundary;
      } else {
        // The scroller's bottom edge may itself be occluded (viewport end, open math keyboard).
        const keyboard = window.mathVirtualKeyboard;
        const keyboardTop =
          keyboard?.visible && keyboard.boundingRect.height > 0 ? keyboard.boundingRect.top : Infinity;
        const bottomBoundary = Math.min(view.bottom, window.innerHeight, keyboardTop) - REVEAL_GAP;
        if (caret.bottom > bottomBoundary) delta = caret.bottom - bottomBoundary;
        else if (caret.top < topBoundary) delta = caret.top - topBoundary;
        // Scrolling down lifts the co-scrolling popover — stop before its top edge leaves the screen.
        if (delta > 0 && dropdown) delta = Math.min(delta, Math.max(0, dropdown.top - REVEAL_GAP));
      }
      // Near the document's end there is nothing left to scroll past — keep the missing room
      // present as bottom padding (same trick as the student keyboard's lift).
      if (anchorSticky) {
        const content = getScrollContent(el, scroller);
        if (content) {
          if (!paddedContent) {
            paddedContent = content;
            basePaddingStyle = content.style.paddingBottom;
            basePaddingPx = parseFloat(getComputedStyle(content).paddingBottom) || 0;
          }
          const scrollHeightBase = scroller.scrollHeight - appliedPadding;
          const required = Math.ceil(scroller.scrollTop + delta - (scrollHeightBase - scroller.clientHeight));
          const next = Math.max(appliedPadding, required);
          if (next !== appliedPadding) {
            content.style.paddingBottom = `${basePaddingPx + next}px`;
            appliedPadding = next;
          }
        }
      }
      // scrollTo, not scrollBy: a relative scroll composes with a still-animating one and lands off-mark.
      const targetTop = Math.min(
        Math.max(scroller.scrollTop + delta, 0),
        scroller.scrollHeight - scroller.clientHeight
      );
      const actualDelta = targetTop - scroller.scrollTop;
      const converging =
        Math.abs(targetTop - lastIssuedTarget) <= 1 && Math.abs(actualDelta) < Math.abs(targetTop - lastObservedTop);
      lastObservedTop = scroller.scrollTop;
      if (actualDelta !== 0 && !converging) {
        scroller.scrollTo({ top: targetTop, behavior: 'smooth' });
        lastIssuedTarget = targetTop;
      }

      // Gap of last resort when scrolling can't separate the caret from the popover: nudge the
      // popover up via margin (so floating-ui's top/left stays untouched), from predicted end positions.
      if (dropdownEl && dropdown) {
        const overlapsHorizontally = caret.right > dropdown.left && caret.left < dropdown.right;
        const dropdownShiftByScroll = anchorSticky ? 0 : actualDelta;
        const baseBottomAfter = dropdown.bottom + appliedShift - dropdownShiftByScroll;
        const baseTopAfter = dropdown.top + appliedShift - dropdownShiftByScroll;
        const caretTopAfter = caret.top - actualDelta;
        const needed = overlapsHorizontally ? Math.ceil(baseBottomAfter + REVEAL_GAP - caretTopAfter) : 0;
        const room = Math.floor(baseTopAfter - REVEAL_GAP);
        appliedShift = Math.min(Math.max(needed, 0), Math.max(room, 0));
        dropdownEl.style.marginTop = appliedShift > 0 ? `-${appliedShift}px` : '';
      }

      return Math.abs(delta);
    };

    // The keyboard's slide and the smooth scroll still move the goal mid-flight; each pass is
    // self-correcting, so re-run until the caret settles (bounded to ~2s).
    let attempts = 0;
    let timer = window.setTimeout(function run() {
      const remaining = reveal();
      attempts += 1;
      if (attempts < 8 && (remaining > 1 || attempts < 4)) timer = window.setTimeout(run, 300);
    }, 50);
    return () => {
      window.clearTimeout(timer);
      if (dropdownRef.current) dropdownRef.current.style.marginTop = '';
      if (paddedContent) paddedContent.style.paddingBottom = basePaddingStyle;
    };
  }, [opened, editor]);

  const handleOpen = () => {
    setLatex('');
    open();
  };

  const handleInsert = () => {
    const value = latex.trim();
    if (value && editor) {
      editor
        .chain()
        .focus()
        .insertContent({ type: INLINE_MATH_NODE_NAME, attrs: { latex: value } })
        .run();
    }
    close();
  };

  return (
    <Popover
      opened={opened}
      onClose={close}
      position="bottom"
      width={360}
      withinPortal={withinPortal}
      closeOnClickOutside={false}
      trapFocus={false}
    >
      <Popover.Target>
        <RichTextEditor.Control
          title={t('title')}
          aria-label={t('title')}
          active={opened}
          onClick={opened ? close : handleOpen}
        >
          <MathKeyboard />
        </RichTextEditor.Control>
      </Popover.Target>
      {/* Lift marker only when portalled: an inline dropdown scrolls with its container instead. */}
      <Popover.Dropdown ref={dropdownRef} {...(withinPortal ? MATH_KEYBOARD_LIFT_PROPS : {})}>
        <Stack gap="sm">
          <MathLiveField value={latex} onChange={setLatex} autoFocus />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" size="xs" onClick={close}>
              {t('cancel')}
            </Button>
            <Button size="xs" onClick={handleInsert} disabled={!latex.trim()}>
              {t('insert')}
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
